import { useState, useEffect } from 'react'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Loader2, Play, ArrowRight } from 'lucide-react'

interface PermissionInfo {
  key: string
  label: string
  description: string
}

interface TestResult {
  permission: string
  description: string
  graph_endpoint: string
  request?: { method: string; url: string }
  response: {
    status: number
    success: boolean
    elapsed_ms: number
    body?: unknown
    error?: string
  }
  account?: { page_id: string; page_name: string | null }
  timestamp: string
}

export function PermissionAudit() {
  const [permissions, setPermissions] = useState<PermissionInfo[]>([])
  const [account, setAccount] = useState<{ page_id: string; page_name: string | null } | null>(null)
  const [results, setResults] = useState<Record<string, TestResult>>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})
  const [initialLoading, setInitialLoading] = useState(true)

  useEffect(() => {
    api.get<{ permissions: PermissionInfo[]; account: typeof account }>('/permission-audit')
      .then(data => {
        setPermissions(data.permissions)
        setAccount(data.account)
      })
      .finally(() => setInitialLoading(false))
  }, [])

  const testPermission = async (key: string) => {
    setLoading(prev => ({ ...prev, [key]: true }))
    try {
      const result = await api.post<TestResult>(`/permission-audit/${key}`)
      setResults(prev => ({ ...prev, [key]: result }))
    } catch (e) {
      setResults(prev => ({
        ...prev,
        [key]: {
          permission: key,
          description: '',
          graph_endpoint: '',
          response: { status: 0, success: false, elapsed_ms: 0, error: e instanceof Error ? e.message : 'Erro' },
          timestamp: new Date().toISOString(),
        },
      }))
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }))
    }
  }

  const testAll = async () => {
    for (const perm of permissions) {
      await testPermission(perm.key)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Teste de Permissões — Meta Graph API</h1>
          <p className="text-zinc-400 mt-1">
            Dispare chamadas individuais do Blissf para a API do Facebook para cada permissão solicitada.
          </p>
          {account && (
            <p className="text-zinc-500 text-sm mt-1">
              Página: <span className="text-zinc-300">{account.page_name}</span> (ID: {account.page_id})
            </p>
          )}
        </div>
        <Button onClick={testAll} variant="outline" className="gap-2">
          <Play className="w-4 h-4" />
          Testar Todas
        </Button>
      </div>

      <div className="space-y-3">
        {permissions.map(perm => {
          const result = results[perm.key]
          const isLoading = loading[perm.key]

          return (
            <div key={perm.key} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-white font-semibold text-sm">{perm.label}</code>
                    {result && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        result.response.success
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {result.response.success ? `${result.response.status} OK` : `${result.response.status || 'ERRO'}`}
                        {result.response.elapsed_ms ? ` · ${result.response.elapsed_ms}ms` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-sm mt-0.5">{perm.description}</p>
                </div>

                <Button
                  onClick={() => testPermission(perm.key)}
                  disabled={isLoading}
                  size="sm"
                  className="gap-2 shrink-0"
                >
                  {isLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ArrowRight className="w-4 h-4" />}
                  {isLoading ? 'Chamando...' : 'Disparar'}
                </Button>
              </div>

              {/* Result */}
              {result && (
                <div className={`border-t px-4 py-3 ${
                  result.response.success ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'
                }`}>
                  <div className="flex items-start gap-2">
                    {result.response.success
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Request info */}
                      <div className="text-xs space-y-1">
                        <p className="text-zinc-400">
                          <span className="text-zinc-500">Chamada:</span>{' '}
                          <code className="text-amber-300">blissf.com</code>
                          <span className="text-zinc-600 mx-1">&rarr;</span>
                          <code className="text-blue-300">graph.facebook.com</code>
                        </p>
                        <p className="text-zinc-400">
                          <span className="text-zinc-500">Endpoint:</span>{' '}
                          <code className="text-zinc-300">{result.graph_endpoint}</code>
                        </p>
                      </div>

                      {/* Response body */}
                      <details>
                        <summary className="text-zinc-500 text-xs cursor-pointer hover:text-zinc-300">
                          Ver resposta completa da Graph API
                        </summary>
                        <pre className="mt-2 text-xs text-zinc-400 bg-zinc-950 rounded p-3 overflow-x-auto max-h-48">
                          {JSON.stringify(result.response.body || result.response.error, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
